import React from "react";
import { useForm } from "react-hook-form";
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { useStagePositionAvailability } from "../../hooks/useStagePositionAvailability";

export interface StageFormValues {
  name: string;
  position: number | null;
  description: string;
}

interface StageFormProps {
  initialData: StageFormValues;
  loading?: boolean;
  readOnly?: boolean;
  onSubmit?: (values: StageFormValues) => void;
}

const StageForm: React.FC<StageFormProps> = ({
  initialData,
  loading = false,
  readOnly = false,
  onSubmit,
}) => {
  const formMethods = useForm<StageFormValues>({
    defaultValues: initialData,
  });

  const {
    handleSubmit,
    control,
    formState: { isSubmitting },
    watch,
  } = formMethods;

  const nameValue = watch("name");
  const positionValue = watch("position");

  const { status: positionStatus, message: positionMessage } =
    useStagePositionAvailability(
      positionValue,
      initialData.position ?? undefined,
    );

  const positionInvalid =
    positionValue === null ||
    Number.isNaN(positionValue) ||
    positionValue < 0 ||
    positionStatus === "loading" ||
    positionStatus === "invalid";

  const handleFormSubmit = (data: StageFormValues) => {
    onSubmit?.(data);
  };

  return (
    <Form {...formMethods}>
      <form className="space-y-4" onSubmit={handleSubmit(handleFormSubmit)}>
        <FormField
          control={control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Name *</FormLabel>
              <FormControl>
                <Input disabled={readOnly} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name="position"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Position *</FormLabel>
              <FormControl>
                <Input
                  disabled={readOnly}
                  min={0}
                  name={field.name}
                  onBlur={field.onBlur}
                  onChange={(e) =>
                    field.onChange(
                      e.target.value === "" ? null : e.target.valueAsNumber,
                    )
                  }
                  ref={field.ref}
                  type="number"
                  value={field.value ?? ""}
                />
              </FormControl>
              {!readOnly && positionMessage && (
                <p
                  className={`text-xs mt-1 ${
                    positionStatus === "invalid"
                      ? "text-red-500"
                      : "text-green-500"
                  }`}
                >
                  {positionMessage}
                </p>
              )}
              <p className="text-xs mt-1 text-gray-500 dark:text-gray-400">
                Lower positions are visible to more companies (Core = 1; 0 =
                full access / Legacy).
              </p>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Description</FormLabel>
              <FormControl>
                <Textarea disabled={readOnly} rows={4} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {!readOnly && (
          <div className="flex justify-end">
            <Button
              disabled={
                loading || isSubmitting || !nameValue?.trim() || positionInvalid
              }
              type="submit"
            >
              {loading || isSubmitting ? "Saving..." : "Save"}
            </Button>
          </div>
        )}
      </form>
    </Form>
  );
};

export default StageForm;
